/**
 * Cognito Hosted UI federation — "Sign in with Google".
 *
 * The site initiates Authorization Code with PKCE: it generates a verifier,
 * stashes it (and a CSRF `state`) in sessionStorage, then redirects the user
 * to /oauth2/authorize. Cognito drives the user through Google's consent and
 * redirects back to /auth/callback?code=...&state=..., where we trade the
 * code server-side at /auth/oauth/exchange (lib/api.ts :: exchangeOAuthCode).
 *
 * Why server-side exchange (not /oauth2/token directly): keeps the API
 * surface uniform (same TokenSet shape as /auth/login), insulates the site
 * from Cognito-specific error mapping, and lets the API rotate the Hosted
 * UI domain without a frontend redeploy.
 */

const DEFAULT_DOMAIN = "minuta-dev.auth.us-east-1.amazoncognito.com";
const DEFAULT_CLIENT_ID = "12p8kc554vsrebg7900vg1ukvg";

export const COGNITO_DOMAIN =
  import.meta.env.VITE_COGNITO_HOSTED_UI_DOMAIN || DEFAULT_DOMAIN;
export const COGNITO_CLIENT_ID =
  import.meta.env.VITE_COGNITO_APP_CLIENT_ID || DEFAULT_CLIENT_ID;

/** True when the Hosted UI is configured. Renderers gate the Google button on this. */
export const OAUTH_ENABLED = COGNITO_DOMAIN !== "" && COGNITO_CLIENT_ID !== "";

const STATE_KEY = "minuta.oauth.state";
const VERIFIER_KEY = "minuta.oauth.verifier";

interface PKCE {
  verifier: string;
  challenge: string;
}

// 32 random bytes → ~43 url-safe base64 chars (within the 43–128 RFC 7636 range).
function randomString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

function base64url(bytes: Uint8Array | ArrayBuffer): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = "";
  for (let i = 0; i < view.length; i++) bin += String.fromCharCode(view[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function generatePKCE(): Promise<PKCE> {
  const verifier = randomString(32);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return { verifier, challenge: base64url(digest) };
}

/** Where Cognito should redirect after consent. Always the site's own origin. */
export function callbackURL(): string {
  return `${window.location.origin}/auth/callback`;
}

/**
 * Begin a federated sign-in. Stashes the PKCE verifier + CSRF state in
 * sessionStorage and navigates the browser to /oauth2/authorize. Throws if
 * the Hosted UI is not configured.
 *
 * `provider` defaults to "Google" — that's the only IdP wired today, but
 * adding more is just a matter of extending the union type.
 */
export async function startGoogleLogin(): Promise<void> {
  if (!OAUTH_ENABLED) {
    throw new Error("OAuth is not configured on this build.");
  }

  const { verifier, challenge } = await generatePKCE();
  const state = randomString(16);

  // sessionStorage scopes the values to the current tab — exactly the
  // window the user has to come back through to complete the flow. If
  // they close the tab, the secrets vanish and we naturally invalidate
  // any in-flight code that arrives later.
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, verifier);

  const url = new URL(`https://${COGNITO_DOMAIN}/oauth2/authorize`);
  url.searchParams.set("identity_provider", "Google");
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", COGNITO_CLIENT_ID);
  url.searchParams.set("redirect_uri", callbackURL());
  url.searchParams.set("scope", "openid email profile");
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", "S256");

  window.location.assign(url.toString());
}

/**
 * Read + clear the values stashed at the start of the flow. Returns null when
 * either is missing (state validation fails) or when state doesn't match.
 *
 * The values are cleared even on a state mismatch so a forged callback can't
 * keep the secrets sitting in storage indefinitely.
 */
export function consumeOAuthState(receivedState: string | null): string | null {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  if (!expectedState || !verifier) return null;
  if (receivedState !== expectedState) return null;
  return verifier;
}
